<template>
  <div v-if="dataLoaded">

    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="$router.push(`/reaction`)"
          icon="keyboard_backspace"
        />
      </div>

      <div class="col-6">
        <q-btn
          class="capitalize ctrl-button right q-ml-md"
          :color="readOnly ? 'grey-5' :'ctrl-turquoise'"
          :label="readOnly ? getString('action.edit') : getString('action.save')"
          @click="saveChanges()"
          :icon-right="readOnly ? 'edit' : 'save'"
        />
        <q-btn v-if="data.id"
          class="q-ml-md capitalize ctrl-button right"
          icon-right="delete"
          color="negative capitalize"
          :label="$store.state.stringsStore.strings.action.delete"
          @click="deleteReaction()"
          align="right"
        />
      </div>
    </div>

    <div class="row gutter-sm q-mb-lg">
      <!-- General Information -->
      <div class="col-md-6 col-sm-12 col-xs-12">
        <p class="card-title">
            {{ getString('reaction') }}
        </p>
        <div class="card-block">
          <!-- Name -->
          <q-field :error="$v.data.user.name.$error" class="q-mb-md">
            <q-input type="text" v-model="$v.data.user.name.$model"
              :disable="readOnly"
              :float-label="getString('name')">
            </q-input>
          </q-field>

          <!-- Additional -->
          <q-field class="q-mb-md">
            <q-input type="text" v-model="data.additional"
              :disable="readOnly"
              :float-label="getString(null, 'Additional', 'Aanvullend')">
            </q-input>
          </q-field>

          <!-- Additional -->
          <q-field class="q-mb-md">
            <q-input type="text" v-model="data.url"
              :disable="readOnly"
              :float-label="getString('url')">
            </q-input>
          </q-field>

          <q-field class="q-mb-md"
            helper="Rating"
          >
            <q-rating
                color="red-5"
                v-model="data.rating"
                :disabled="readOnly"
                size="2rem"
                :max="5"
            />
          </q-field>
        </div>
      </div>
      <!-- Publication Information -->
      <div class="col-md-6 col-sm-12 col-xs-12">
        <p class="card-title">
            {{ getString(null, 'Publication', 'Publicatie') }}
        </p>
        <div class="card-block">
          <!-- Name -->
          <q-field class="q-mb-md" :error="$v.data.projects.$error">
            <chips-autocomplete
              class="q-pb-md"
              :disable="readOnly"
              :selection="$v.data.projects.$model"
              :name="'Books'"
              @change="$v.data.projects.$touch()"
              :labelTerm="'title'"
              :endpoint="'project'"
              :resultLabel="'projects'"
            />
          </q-field>
        </div>
      </div>

    </div>

    <!-- Content editor -->
    <div class="row gutter-sm q-mb-lg q-mt-sm">
      <div class="full-width">
        <p class="card-title">{{ getString('block.type.html.content') }}</p>
        <div class="card-block scroll" :class="{'editor-error' : $v.data.reaction.$error }">

          <!-- Field: HTML Editor -->
          <tiny-editor
            tinymce-script-src="https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.11/tinymce.min.js"
            :init="tinyConfig()"
            ref="tm"
            :disabled="readOnly"
            v-model="$v.data.reaction.$model">
          </tiny-editor>
        </div>
      </div>
    </div>

    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import saveChanges from '@/mixins/detailViews/saveChanges';
import suggestUrl from '@/mixins/suggestUrl';
import getString from '@/helpers/GetStrings';
import ChipsAutocomplete from '@/components/ChipsAutocomplete.vue';
import EventBus from '@/event-bus';

const TinyEditor = require('@tinymce/tinymce-vue').default;

export default {
  validations: {
    data: {
      reaction: {
        required,
      },
      user: {
        name: {
          required,
        },
      },
      projects: {
        required,
      },
    },
  },
  data() {
    return {
      noCategory: null,
      categories: [],
      dataLoaded: false,
      endpoint: 'reaction',
      responseData: {
        multiple: 'reactions',
        single: 'reaction',
      },
      module: 'reaction',
      data: {
        additional: '',
        url: '',
        user: {
          name: '',
        },
        reaction: '',
        projects: [],
      },
    };
  },

  components: {
    TinyEditor,
    ChipsAutocomplete,
  },

  mixins: [
    suggestUrl,
    saveChanges,
  ],
  methods: {

    tinyConfig() {
      return {
        readOnly: false,
        height: 200,
        menubar: false,
        branding: false,
        browser_spellcheck: true,
        plugins: ['link'],
        toolbar: [
          'undo redo | bold | pagebreak | link',
        ],
        style_formats: [
          { title: 'Paragraph', format: 'p' },
          { title: 'Heading 1', format: 'h1' },
          { title: 'Heading 2', format: 'h2' },
        ],
        pagebreak_split_block: false,
      };
    },

    getString,

    deleteReaction() {
      // Dialog
      this.$q.dialog({
        title: '',
        message: this.getString('module.library.delete.title.single'),
        ok: this.$store.state.stringsStore.strings.dialog.confirm.ok,
        cancel: this.$store.state.stringsStore.strings.dialog.confirm.cancel,
      }).then(() => {
        // delete call
        this.axios.delete(`reaction/${this.data.id}`)
          .then(() => {
            // Refresh data to see the result
            this.$q.notify({
              message: this.getString('upload.delete.success').replace('%file%', this.getString('reaction')),
              position: 'top-right',
              timeout: 1000,
              color: 'positive',
            });
            this.$router.push('/reaction');
          })
          .catch((error) => {
            // Error
            console.warn(error);
          });
      }).catch(() => {
        console.warn('Not removed');
      });
    },

    requestParams() {
      const requiredFields = {
        reaction: this.data.reaction,
        name: this.data.user.name,
        additional: this.data.additional,
        project: this.data.projects.map(project => project.id),
      };

      const optional = {
        url: this.data.url,
        rating: this.data.rating,
      };

      // If a value is null, undefined or empty, remove it from fields
      for (const [key, value] of Object.entries(optional)) {
        if (value === null || value === undefined) delete optional[key];
      }

      // Join required and optional
      const fields = Object.assign(requiredFields, optional);

      return fields;
    },

    async searchProject(terms, done) {
      try {
        // Request items based on terms given
        const results = await this.axios.get(`project?autocomplete=${terms.toLowerCase()}`);

        // Map only the item names/labels... to the suggested array
        const suggested = results.data.projects.map(item => ({ label: item.title, id: item.id }));

        done(suggested);

      // Since an error is thrown when no items are found in autocomplete -> promt new suggestion here.
      } catch (error) {
        console.warn(error.message);
        done([]);
      }
    },
  },

  filters: {
    checkEmpty(value) {
      return value === null || value === '' ? '-' : value;
    },

    capitalize(value) {
      if (!value) return '';
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },

  mounted() {
    if (this.$route.params.id) this.readOnly = true;

    EventBus.$on('chips-autocomplete:projects', (selection) => {
      this.data.projects = selection;
    });
  },

  created() {
    if (this.$route.params.id) {
    // Fetch the selected module details by id.
      this.axios.get(`reaction/${this.$route.params.id}`)
        .then((response) => {
        // In the module detail page, if a given id is not found, we will always get a 200 OK response.
          if (!response.data.reaction) {
          // Redirect to modules overview
            this.$router.push('/reaction');
            return;
          }

          this.data = response.data.reaction;

          this.dataLoaded = true;
        })
        .catch((error) => {
          console.warn(error);

          // Notify if nothing found
          if (error.response.status === 404) {
            this.$q.notify({
              message: getString(`module.${this.module}.edit.error.nothingFound`),
              type: 'warning',
              timeout: 2000,
              position: 'top-right',
            });
          } else {
          // Throw general error notification
            this.$q.notify({
              message: getString('error.general'),
              position: 'top-right',
              timeout: 1000,
              type: 'warning',
            });
          }

          // Redirect to modules overview
          this.$router.push(`/${this.module}`);
        });
    } else {
      // Nothing to load
      this.dataLoaded = true;
    }
  },
};
</script>


<style lang="stylus" scoped>
@import '~variables';
.data-title
  font-weight 700

.data-content
  word-break break-all

.editor-error
  border 1px solid $ctrl-red-lighten-3
</style>
